/**
 * PingMonitor — App-level latency probe.
 *
 * Sends `ping` messages on a fixed interval and measures the round-trip time
 * when the server echoes back via `pong`. Exposes a `currentPingMs` getter and
 * a subscribe/notify mechanism so UI components can update their badge color
 * and label without polling.
 *
 * The monitor is transport-aware only via the small `PingTransport` interface
 * so it stays trivial to unit-test with a stub. ClientNetManager owns the
 * lifecycle (start on join, stop on disconnect).
 */

export interface PingTransport {
  send(type: string, payload: unknown): void;
  onMessage(type: string, callback: (payload: unknown) => void): void;
}

export type PingListener = (ms: number) => void;

export interface PingMonitorOptions {
  intervalMs?: number;
  /** Override `Date.now()` for tests. */
  now?: () => number;
  /** Override `setInterval`/`clearInterval` for tests. */
  setIntervalFn?: (cb: () => void, ms: number) => ReturnType<typeof setInterval>;
  clearIntervalFn?: (handle: ReturnType<typeof setInterval>) => void;
}

const DEFAULT_INTERVAL_MS = 2000;

export class PingMonitor {
  private transport: PingTransport;
  private intervalMs: number;
  private now: () => number;
  private setIntervalFn: (cb: () => void, ms: number) => ReturnType<typeof setInterval>;
  private clearIntervalFn: (handle: ReturnType<typeof setInterval>) => void;

  private handle: ReturnType<typeof setInterval> | null = null;
  private _currentPingMs = 0;
  private listeners: PingListener[] = [];

  constructor(transport: PingTransport, options: PingMonitorOptions = {}) {
    this.transport = transport;
    this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    this.now = options.now ?? (() => Date.now());
    this.setIntervalFn = options.setIntervalFn ?? ((cb, ms) => setInterval(cb, ms));
    this.clearIntervalFn = options.clearIntervalFn ?? ((h) => clearInterval(h));
  }

  /** Begin sending pings + listening for pongs. Idempotent. */
  start(): void {
    if (this.handle !== null) return;

    this.transport.onMessage('pong', (payload) => {
      const sentAt = (payload as { t?: number } | null)?.t;
      if (typeof sentAt !== 'number') return;
      const rtt = Math.round(Math.max(0, this.now() - sentAt));
      // Suppress no-op notifications so downstream UI doesn't repaint on
      // identical samples (common when server + client share a localhost).
      if (rtt === this._currentPingMs) return;
      this._currentPingMs = rtt;
      this.notify();
    });

    // Send the first ping immediately so UI populates without a 2s wait.
    this.sendPing();
    this.handle = this.setIntervalFn(() => this.sendPing(), this.intervalMs);
  }

  /** Stop sending pings. Safe to call multiple times. */
  stop(): void {
    if (this.handle !== null) {
      this.clearIntervalFn(this.handle);
      this.handle = null;
    }
  }

  get currentPingMs(): number {
    return this._currentPingMs;
  }

  /** Subscribe to RTT updates. Returns an unsubscribe handle. */
  subscribe(listener: PingListener): () => void {
    this.listeners.push(listener);
    listener(this._currentPingMs);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx >= 0) this.listeners.splice(idx, 1);
    };
  }

  private sendPing(): void {
    try {
      this.transport.send('ping', { t: this.now() });
    } catch {
      // If the transport is closed mid-tick we just drop the sample; the
      // outer connection-state machine will mark us as reconnecting.
    }
  }

  private notify(): void {
    for (const l of this.listeners) l(this._currentPingMs);
  }
}
